import { useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { VendorInsightsPanel } from "@/components/VendorInsightsPanel";
import { Stethoscope, Warning, ArrowClockwise, Sparkle, CheckCircle, Question } from "@phosphor-icons/react";

const PRIORITY = {
  high: { label: "High", color: "#B23B1F", bg: "#FDECE8" },
  medium: { label: "Medium", color: "#8A6A3B", bg: "#F6EEDF" },
  low: { label: "Low", color: "#4A6B53", bg: "#E8F0EA" },
};
const label = { color: "var(--bk-text-2)" };
const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function Evidence({ items, testId }) {
  if (!items?.length) return null;
  return (
    <ul className="flex flex-wrap gap-1.5 mt-3" data-testid={testId}>
      {items.map((e, i) => (
        <li key={i} className="text-xs font-mono px-2 py-1 rounded-md" style={{ background: "var(--bk-page)", color: "var(--bk-text)" }}>{e}</li>
      ))}
    </ul>
  );
}

function BalancesStrip({ snapshot }) {
  const bs = snapshot?.balance_sheet;
  if (!bs) return null;
  const always = ["Bank", "Card clearing", "Deferred revenue"];
  const cells = [
    ["Bank", bs.bank], ["Cash", bs.cash], ["Card clearing", bs.clearing], ["Deferred revenue", bs.deferred_revenue],
    ["Credit card owed", bs.credit_card], ["Owner's draw", bs.owner_draw],
  ].filter(([k, v]) => always.includes(k) || Math.abs(Number(v) || 0) > 0.004);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="diagnose-balances">
      {cells.map(([k, v]) => (
        <div key={k} className="bk-card p-3">
          <p className="text-[11px]" style={label}>{k}</p>
          <p className="font-mono text-sm font-semibold" style={{ color: Number(v) < 0 ? "#B23B1F" : "var(--bk-text)" }}>{money(v)}</p>
        </div>
      ))}
      <div className="bk-card p-3">
        <p className="text-[11px]" style={label}>Balance sheet</p>
        <p className="text-sm font-semibold" style={{ color: bs.balanced ? "var(--bk-success)" : "#B23B1F" }} data-testid="diagnose-bs-status">{bs.balanced ? "Balanced" : `Off by ${money(bs.difference)}`}</p>
      </div>
      <div className="bk-card p-3">
        <p className="text-[11px]" style={label}>Trial balance</p>
        <p className="text-sm font-semibold" style={{ color: snapshot.trial_balance?.balanced ? "var(--bk-success)" : "#B23B1F" }} data-testid="diagnose-tb-status">{snapshot.trial_balance?.balanced ? "Balanced" : "Out of balance"}</p>
      </div>
    </div>
  );
}

export function DiagnoseBooks({ businessId, initial }) {
  const [description, setDescription] = useState(initial?.input || "");
  const [diagnosis, setDiagnosis] = useState(initial || null);
  const [loading, setLoading] = useState(false);
  const isLive = !!diagnosis?.snapshot;

  const run = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/diagnose`, { description });
      setDiagnosis(data);
      toast.success(`Diagnosis ready — ${data.issues.length} finding${data.issues.length !== 1 ? "s" : ""} from live data`);
    } catch (err) {
      toast.error(describeApiError(err, "Rowan couldn't finish the diagnosis."), { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="diagnose-panel">
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--bk-success-bg)" }}>
          <Stethoscope size={24} weight="duotone" color="var(--bk-success)" />
        </span>
        <div>
          <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="diagnose-heading">Diagnose My Books</h3>
          <p className="text-sm mt-1 max-w-2xl" style={label}>
            Rowan reads this business's live books — balance sheet, trial balance, bank and clearing balances, reconciliation imports,
            uncategorized transactions, open flags, receipts, invoices and period close — and lists what needs fixing, each with the figure behind it.
            Anything the data can't prove is labelled “Needs verification”.
          </p>
        </div>
      </div>

      <form onSubmit={run} className="bk-card p-6 space-y-3">
        <label className="text-xs font-semibold uppercase tracking-wider" style={label} htmlFor="diagnose-input">Optional: anything you've noticed</label>
        <textarea id="diagnose-input" data-testid="diagnose-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
          placeholder="e.g. the bank balance looks high, or a vendor was paid twice — Rowan will keep this with the report"
          className="w-full px-4 py-3 rounded-md border text-sm outline-none focus:ring-2 resize-y" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
        <button type="submit" disabled={loading} data-testid="diagnose-run" className="bk-btn-primary px-6 py-2.5 rounded-md text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {diagnosis ? <ArrowClockwise size={18} weight="bold" /> : <Sparkle size={18} weight="fill" />}
          {loading ? "Reading the ledger…" : diagnosis ? "Re-run on current data" : "Run diagnosis on live data"}
        </button>
      </form>

      {diagnosis && (
        <div className="space-y-4" data-testid="diagnose-result">
          <div className="bk-card p-6" style={{ borderLeft: "3px solid var(--bk-accent)" }}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Summary</p>
              <p className="text-xs" style={label} data-testid="diagnose-meta">
                {isLive ? `Checked ${when(diagnosis.generated_at)} · live data as of ${diagnosis.snapshot.as_of}` : `Generated ${when(diagnosis.generated_at)} · older format — re-run for live-data findings`}
              </p>
            </div>
            <p className="text-base leading-relaxed" style={{ color: "var(--bk-text)" }} data-testid="diagnose-summary">{diagnosis.summary}</p>
            {isLive && <p className="text-xs mt-3" style={label} data-testid="diagnose-sources">Sources: {diagnosis.data_sources.join(" · ")}</p>}
          </div>

          {isLive && <BalancesStrip snapshot={diagnosis.snapshot} />}

          <div className="space-y-3" data-testid="diagnose-issues">
            {diagnosis.issues.length === 0 && (
              <div className="bk-card p-5 text-sm" style={{ color: "var(--bk-success)" }} data-testid="diagnose-no-issues">Nothing needs fixing in the ledger right now.</div>
            )}
            {diagnosis.issues.map((it, i) => {
              const p = PRIORITY[it.priority] || PRIORITY.medium;
              return (
                <div key={i} data-testid={`diagnose-issue-${i}`} className="bk-card p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h4 className="font-semibold text-base flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
                      <Warning size={18} weight="fill" color={p.color} /> {it.title}
                    </h4>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full shrink-0" style={{ background: p.bg, color: p.color }}>{p.label}</span>
                  </div>
                  {it.source && <p className="text-[11px] mt-1 uppercase tracking-wider" style={label}>From: {it.source}</p>}
                  <p className="text-sm mt-2 leading-relaxed" style={label}>{it.why}</p>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--bk-text)" }}>
                    <span className="font-semibold" style={{ color: "var(--bk-success)" }}>Fix: </span>{it.action}
                  </p>
                  <Evidence items={it.evidence} testId={`diagnose-evidence-${i}`} />
                </div>
              );
            })}
          </div>

          {isLive && diagnosis.needs_verification?.length > 0 && (
            <div className="space-y-3" data-testid="diagnose-needs-verification">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={label}>Needs verification — Rowan can't see the document</p>
              {diagnosis.needs_verification.map((v, i) => (
                <div key={i} className="bk-card p-5" style={{ borderStyle: "dashed" }} data-testid={`diagnose-verify-${i}`}>
                  <h4 className="font-semibold text-base flex items-center gap-2" style={{ color: "var(--bk-text)" }}><Question size={18} weight="bold" color="#8A6A3B" /> {v.title}</h4>
                  {v.source && <p className="text-[11px] mt-1 uppercase tracking-wider" style={label}>From: {v.source}</p>}
                  <p className="text-sm mt-2 leading-relaxed" style={label}>{v.why}</p>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--bk-text)" }}><span className="font-semibold" style={{ color: "var(--bk-success)" }}>Next: </span>{v.action}</p>
                  <Evidence items={v.evidence} testId={`diagnose-verify-evidence-${i}`} />
                </div>
              ))}
            </div>
          )}

          {isLive && diagnosis.looks_right?.length > 0 && (
            <div className="bk-card p-5" data-testid="diagnose-looks-right">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "var(--bk-success)" }}>Looks right</p>
              <ul className="space-y-1.5">
                {diagnosis.looks_right.map((x, i) => (
                  <li key={i} className="text-sm flex items-start gap-2" style={{ color: "var(--bk-text)" }}><CheckCircle size={16} weight="fill" color="var(--bk-success)" className="mt-0.5 shrink-0" /> {x}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <VendorInsightsPanel
        businessId={businessId}
        title="Money Leak Detector"
        subtitle="Rowan watches your repeat vendors, spots cost creep, and helps you ask for a better rate before habit becomes overhead."
        testIdPrefix="money-leak-detector"
      />
    </div>
  );
}
